import { useNavigate } from 'react-router-dom'

// /drink — the art director's signature drink, as a full-page recipe card.
// Designed to be screenshot-worthy and shareable, like /broken. Discovered
// via the CommandPalette ("the usual") or a direct link. Not auth-gated,
// just brand.
export default function TheUsual() {
  const navigate = useNavigate()

  return (
    <div className="crt-scanlines fixed inset-0 z-[150] bg-bg-primary text-text-primary overflow-auto">
      <div className="absolute inset-0 grid-backdrop pointer-events-none opacity-25" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(255,170,0,0.10) 0%, rgba(255,170,0,0.04) 35%, transparent 70%)',
          filter: 'blur(50px)',
          animation: 'pulse-soft 5s ease-in-out infinite',
        }}
      />

      <div className="relative z-10 min-h-full flex flex-col items-center justify-between px-6 py-10 md:py-16">
        {/* Header mark */}
        <div className="w-full max-w-4xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-neon/10 border border-neon/30 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#39FF14"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
              </svg>
            </div>
            <div>
              <p className="brand-wordmark text-base text-neon neon-text leading-none">
                FRANKENVIBES <span className="text-text-primary">LAB</span>
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted mt-1">
                / the art director's signature
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted hover:text-neon px-3 py-1.5 rounded-md border border-border hover:border-neon transition-colors cursor-pointer"
          >
            ← back to the lab
          </button>
        </div>

        {/* Hero title + subtitle */}
        <div className="max-w-3xl text-center space-y-4 py-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-neon/70">
            / the usual
          </p>
          <h1 className="brand-wordmark text-5xl md:text-7xl leading-[1.05] text-text-primary">
            A Rooibos Gin &amp; Tonic,{' '}
            <span className="italic text-neon">darling</span>.
          </h1>
          <p className="text-base md:text-lg text-text-secondary italic max-w-2xl mx-auto leading-relaxed">
            Short tumbler. Naartjie peel, not lemon. One cardamom pod. Single
            ice cube, round if you have it. Usually the second of the day.
            Frank has the tonic cold.
          </p>
        </div>

        {/* Recipe card */}
        <div className="w-full max-w-2xl rounded-2xl border border-border bg-bg-card p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon">
              ● recipe · serves one
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">
              brew time · 90 seconds
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted">
                / you will need
              </h3>
              <ul className="space-y-2 text-sm text-text-primary">
                <Ingredient>50 ml rooibos-infused gin</Ingredient>
                <Ingredient>
                  Indian tonic water,{' '}
                  <span className="italic text-text-muted">cold</span>
                </Ingredient>
                <Ingredient>
                  One strip of{' '}
                  <span className="text-neon">naartjie peel</span>
                  <span className="italic text-text-muted"> (not lemon)</span>
                </Ingredient>
                <Ingredient>
                  One <span className="text-neon">green cardamom pod</span>,
                  lightly crushed
                </Ingredient>
                <Ingredient>
                  One large round ice cube{' '}
                  <span className="italic text-text-muted">(no shards)</span>
                </Ingredient>
              </ul>
            </div>

            <div className="space-y-3">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted">
                / method
              </h3>
              <ol className="space-y-2 text-sm text-text-secondary">
                <Step n={1}>Put the ice in first. Never last.</Step>
                <Step n={2}>Pour the gin over it. Slowly, pet.</Step>
                <Step n={3}>
                  Crush the cardamom between your palms, drop it in.
                </Step>
                <Step n={4}>Twist the naartjie peel over the glass.</Step>
                <Step n={5}>Tonic until the ice lifts. Not a splash more.</Step>
                <Step n={6}>Sip. Approve. Pour another.</Step>
              </ol>
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-2">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted">
              / house rules
            </h3>
            <ul className="text-[12px] text-text-secondary space-y-1.5 italic">
              <li>— No lemon. <span className="not-italic text-text-muted">Ever.</span></li>
              <li>— No highball glasses. <span className="not-italic text-text-muted">This is not a hotel bar.</span></li>
              <li>— Coaster. <span className="not-italic text-text-muted">Always.</span></li>
              <li>— If Frank is having one too, pour his first. <span className="not-italic text-text-muted">It's a lab tradition.</span></li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="max-w-2xl text-center pt-10 space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted">
            / the fine print
          </p>
          <p className="text-[11px] text-text-muted italic leading-relaxed">
            The art director drinks this one. Frank drinks whatever was
            nearest. You should drink whatever makes you feel slightly
            dangerous on a Tuesday. We are not affiliated with any brand of
            gin, tonic, cardamom, naartjie, ice cube, or Tuesday.
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted pt-3">
            frankenvibes-lab.pages.dev/drink
          </p>
        </div>
      </div>
    </div>
  )
}

function Ingredient({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-baseline gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-neon shrink-0 translate-y-[4px]" />
      <span>{children}</span>
    </li>
  )
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-baseline gap-3">
      <span className="font-mono text-[10px] font-semibold text-neon shrink-0 w-4">
        {String(n).padStart(2, '0')}
      </span>
      <span>{children}</span>
    </li>
  )
}
